import { Controller } from "@hotwired/stimulus";

// Slightly longer than the .7s flash animation in _filter-chip.scss, so the
// pulse class is cleared only after the animation has finished.
const FLASH_FADE_MS = 800;

// Runs inside a filter chip's dropdown panel: search filtering, multi/single
// selection, keyboard navigation, autofocus on open, and hoisting selected rows
// into a synthetic "selected" section at the top. Hoisting fires instantly on
// each toggle (mirroring the remote panel, which pins selected rows above its
// Turbo Frame), with a redundant pass on close as a safety net.
export default class extends Controller {
  static targets = [
    "searchInput",
    "searchClear",
    "item",
    "empty",
    "section",
    "selectedSection",
    "divider",
    "sections",
    "resultsFrame",
    "nextPageSentinel",
  ];
  static values = {
    selectionMode: { type: String, default: "multi" },
    // Single-select panels refuse to deselect the current row. Used by the
    // 3-segment chip's operator panel (exactly-one contract).
    selectionRequired: { type: Boolean, default: false },
    // Order-sensitive panels (e.g. the operator segment) opt out of hoisting
    // selected rows to the top. Multi always hoists regardless.
    preserveOrder: { type: Boolean, default: false },
    url: String,
    emptyMode: { type: String, default: "fetch-on-open" },
    debounce: { type: Number, default: 200 },
  };

  connect() {
    this._observeNextPageSentinel();
    this._flashTimers = new WeakMap();
  }

  disconnect() {
    clearTimeout(this.remoteSearchTimeout);
    if (this._nextPageObserver) this._nextPageObserver.disconnect();
  }

  focusSearchIfOwner(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    if (!this.hasSearchInputTarget) return;
    // The dropdown's menu element. Used in the deferred callback to make
    // sure we don't focus into a panel that closed inside this same frame.
    const ownerMenu = event.target.querySelector("[data-dropdown-target='menu']");
    // Defer one frame so Floating UI positioning settles before focus.
    requestAnimationFrame(() => {
      if (!this.hasSearchInputTarget) return;
      // Bail if the dropdown was closed (or the panel removed) between
      // dropdown:shown and this frame; otherwise we'd focus a hidden input.
      if (ownerMenu && ownerMenu.classList.contains("hidden")) return;
      if (!this.searchInputTarget.isConnected) return;
      this.searchInputTarget.focus();
    });
  }

  search() {
    if (this.hasUrlValue) {
      this._remoteSearch();
      return;
    }

    const query = (this.searchInputTarget.value || "").trim().toLowerCase();
    let totalVisible = 0;

    this.itemTargets.forEach((item) => {
      const name = (item.dataset.name || "").toLowerCase();
      const matches = query === "" || name.includes(query);
      item.hidden = !matches;
      if (matches) totalVisible += 1;
    });

    // Divider i stays only if sections[i] and sections[i+1] both have a
    // visible item.
    const sectionVisibility = this.sectionTargets.map((section) => {
      const itemsInSection = section.querySelectorAll(
        "[data-filter-option-list-target='item']"
      );
      const anyVisible = Array.from(itemsInSection).some((item) => !item.hidden);
      section.hidden = !anyVisible;
      return anyVisible;
    });

    this.dividerTargets.forEach((divider, i) => {
      divider.hidden = !(sectionVisibility[i] && sectionVisibility[i + 1]);
    });

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = totalVisible > 0;
    }

    if (this.hasSearchClearTarget) {
      this.searchClearTarget.hidden = this.searchInputTarget.value === "";
    }

    this._resetRovingTabindex();
  }

  frameLoaded() {
    this._dropExcludedResultRows();
    this._resetRovingTabindex();
    // A freshly loaded page brings its own trailing sentinel; start watching it.
    // Bubbling turbo:frame-load from nested sentinels reaches the results frame's
    // action, so this also covers paginated loads.
    this._observeNextPageSentinel();
  }

  clearSearch(event) {
    if (event && event.type === "dropdown:hidden") {
      if (!event.target || !event.target.contains(this.element)) return;
    }
    if (!this.hasSearchInputTarget) return;
    if (this.searchInputTarget.value === "") return;

    this.searchInputTarget.value = "";
    this.search();

    if (event && event.type === "click") {
      this.searchInputTarget.focus();
    }
  }

  toggle(event) {
    const focusTarget = this._nextFocusAfterToggle(event.target);

    if (this.hasUrlValue && this.selectionModeValue === "multi") {
      this._syncRemoteSelection(event);
    } else {
      this._rehomeInstant();
    }

    this._restoreFocusAfterToggle(focusTarget);

    if (event.target.checked) {
      this._flashHoistedItem(event.target.closest("[data-filter-option-list-target='item']"));
    }

    this.dispatch("change", {
      detail: { selected: this.selectedValues },
      bubbles: true,
    });
  }

  // Rewrites every row so a server-rendered multi-selected state self-heals.
  // With selectionRequired, re-clicking the current row is a no-op.
  selectSingle(event) {
    const row = event.currentTarget;
    const wasSelected = row.getAttribute("aria-selected") === "true";
    if (this.selectionRequiredValue && wasSelected) return;

    // Order-sensitive panels (operator) never reshuffle, so they keep focus put.
    const hoists = this.hasUrlValue || this._hoistsSelected();
    const focusTarget = hoists ? this._nextFocusAfterToggle(row) : null;

    const nextSelected = wasSelected ? null : row;

    this.singleRows.forEach((r) => {
      r.setAttribute("aria-selected", r === nextSelected ? "true" : "false");
    });

    if (this.hasUrlValue) {
      this._syncRemoteSingleSelection(nextSelected);
    } else if (this._hoistsSelected()) {
      this._rehomeInstant();
    }

    this._restoreFocusAfterToggle(focusTarget);

    if (hoists && nextSelected) {
      this._flashHoistedItem(nextSelected);
    }

    this.dispatch("change", {
      detail: { selected: this.selectedValues },
      bubbles: true,
    });
  }

  keydown(event) {
    const target = event.target;
    const onSearch = this.hasSearchInputTarget && target === this.searchInputTarget;
    const onFocusable = this._focusableRows.includes(target);

    if (!onSearch && !onFocusable) return;

    if (onSearch && event.key === "ArrowDown") {
      const first = this._visibleFocusableRows[0];
      if (first) {
        event.preventDefault();
        this._focusAndRove(first);
      }
      return;
    }

    if (!onFocusable) return;

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        const visible = this._visibleFocusableRows;
        const idx = visible.indexOf(target);
        // No wrap: ArrowDown on the last row is a no-op.
        if (idx >= 0 && idx < visible.length - 1) {
          this._focusAndRove(visible[idx + 1]);
        }
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        const visible = this._visibleFocusableRows;
        const idx = visible.indexOf(target);
        if (idx === 0) {
          if (this.hasSearchInputTarget) this.searchInputTarget.focus();
        } else if (idx > 0) {
          this._focusAndRove(visible[idx - 1]);
        }
        break;
      }
      case "Home": {
        event.preventDefault();
        const first = this._visibleFocusableRows[0];
        if (first) this._focusAndRove(first);
        break;
      }
      case "End": {
        event.preventDefault();
        const visible = this._visibleFocusableRows;
        const last = visible[visible.length - 1];
        if (last) this._focusAndRove(last);
        break;
      }
      case "Enter":
      case " ": {
        const isEnter = event.key === "Enter";
        // Space toggles checkboxes natively; only intercept it for
        // single-select rows. Enter is intercepted in both modes.
        if (this.selectionModeValue === "single") {
          event.preventDefault();
          target.click();
        } else if (isEnter) {
          event.preventDefault();
          this._applyEnterSelection(target);
        }
        // Enter commits and closes the panel in both modes; Space only toggles.
        if (isEnter) this._requestClose();
        break;
      }
      default:
        break;
    }
  }

  hoistOnClose(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    this.rehome();
  }

  resetIfOwner(event) {
    if (!event.target || !event.target.contains(this.element)) return;
    this.reset();
  }

  // Returns the panel to its pristine state: no query, no hoist. Called
  // when the chip is dismissed so the next open shows everything in order.
  reset() {
    const hadQuery = this.hasSearchInputTarget && this.searchInputTarget.value !== "";
    if (hadQuery) this.searchInputTarget.value = "";

    if (this.hasUrlValue) {
      this._resetRemote();
      return;
    }

    if (hadQuery) this.search();
    this.rehome();
  }

  // Dismiss teardown for remote panels (rehome() no-ops here). The chip already
  // unchecked the rows; drop the stale pinned section and refetch so they return
  // to the server-rendered frame instead of lingering, unchecked, above it.
  _resetRemote() {
    const section = this._remoteSelectedSection;
    if (section) section.remove();

    if (this.hasSearchClearTarget) this.searchClearTarget.hidden = true;

    const query = this.hasSearchInputTarget ? this._searchQuery : "";
    if (this.hasResultsFrameTarget) {
      if (this.emptyModeValue === "search-first" && query === "") {
        this.resultsFrameTarget.removeAttribute("src");
        this.resultsFrameTarget.innerHTML = "";
      } else {
        this.resultsFrameTarget.setAttribute("src", this._remoteSearchUrl());
      }
    }

    this._resetRovingTabindex();
  }

  // Rebuilds sections to match what the server would render for the current
  // selection state, hoisting selected rows to a synthetic top section. Works
  // for both multi (checkbox) and single (aria-selected) panels.
  rehome() {
    // Remote panels keep their selected section above the Turbo Frame and let
    // the server exclude selected rows; the static hoist would drag those rows
    // back into the frame, where the next fetch would wipe them.
    if (this.hasUrlValue) return;
    // Multi always hoists; single hoists unless the panel is order-sensitive.
    if (!this._hoistsSelected()) return;
    if (!this.hasSectionsTarget) return;

    const allItems = Array.from(this.itemTargets);
    const pinnedItems = allItems.filter((li) => li.dataset.pinned === "true");
    const regularItems = allItems.filter((li) => li.dataset.pinned !== "true");

    // Idempotency: invariant is "synthetic selected section exists iff
    // (non-pinned) items are selected". When neither is true the panel is
    // already pristine, so skipping avoids an O(n log n) rebuild on
    // potentially huge option lists. The pinned section is static, so a
    // selected pinned item never forces a rebuild on its own.
    if (
      !this.hasSelectedSectionTarget &&
      !regularItems.some((li) => this._itemSelected(li))
    ) {
      return;
    }

    const container = this.sectionsTarget;

    // Mirror the server's case-insensitive abc sort within each section.
    const byLabel = (a, b) =>
      (a.dataset.name || "").toLowerCase().localeCompare(
        (b.dataset.name || "").toLowerCase(),
      );

    const selectedInOrder = regularItems
      .filter((li) => this._itemSelected(li))
      .sort(byLabel);

    const unselectedByHome = new Map();
    regularItems.forEach((li) => {
      if (this._itemSelected(li)) return;
      const idx = Number(li.getAttribute("data-home-section-index"));
      if (!Number.isFinite(idx) || idx < 0) return;
      if (!unselectedByHome.has(idx)) unselectedByHome.set(idx, []);
      unselectedByHome.get(idx).push(li);
    });
    unselectedByHome.forEach((items) => items.sort(byLabel));

    // Items survive in memory; Stimulus rebinds targets/actions on re-append.
    container.replaceChildren();

    let needsLeadingDivider = false;

    // Pinned section always leads, in its server-rendered (caller) order.
    if (pinnedItems.length > 0) {
      const pinned = this._createSection({ kind: "pinned", index: -2 });
      pinnedItems.forEach((li) => pinned.appendChild(li));
      container.appendChild(pinned);
      needsLeadingDivider = true;
    }

    if (selectedInOrder.length > 0) {
      if (needsLeadingDivider) container.appendChild(this._createDivider());
      const selected = this._createSection({ kind: "selected", index: -1 });
      selectedInOrder.forEach((li) => selected.appendChild(li));
      container.appendChild(selected);
      needsLeadingDivider = true;
    }

    Array.from(unselectedByHome.keys())
      .sort((a, b) => a - b)
      .forEach((homeIndex) => {
        const items = unselectedByHome.get(homeIndex);
        if (!items || items.length === 0) return;
        if (needsLeadingDivider) container.appendChild(this._createDivider());
        const section = this._createSection({ kind: "home", index: homeIndex });
        items.forEach((li) => section.appendChild(li));
        container.appendChild(section);
        needsLeadingDivider = true;
      });

    this._resetRovingTabindex();
  }

  get checkboxes() {
    return this.itemTargets
      .map((item) => item.querySelector("input[type='checkbox']"))
      .filter((cb) => cb !== null);
  }

  get singleRows() {
    return this.itemTargets.filter((item) => item.getAttribute("role") === "option");
  }

  get selectedValues() {
    if (this.selectionModeValue === "single") {
      return this.singleRows
        .filter((r) => r.getAttribute("aria-selected") === "true")
        .map((r) => r.dataset.value);
    }

    return this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value);
  }

  // ─── private helpers ──────────────────────────────────────────────────────

  // Mirrors the server's OptionListComponent#hoist_selected?: multi always
  // hoists; single hoists unless the panel is order-sensitive (operator).
  _hoistsSelected() {
    if (this.selectionModeValue === "multi") return true;
    return !this.preserveOrderValue;
  }

  // Mode-agnostic "is this row selected?": multi reads the checkbox, single
  // reads aria-selected.
  _itemSelected(li) {
    if (this.selectionModeValue === "single") {
      return li.getAttribute("aria-selected") === "true";
    }
    const checkbox = li.querySelector("input[type='checkbox']");
    return !!(checkbox && checkbox.checked);
  }

  // Asks the enclosing dropdown to close (Enter = commit + close). Bubbles to
  // an element-scoped action on the chip/segment, so siblings are unaffected.
  _requestClose() {
    this.dispatch("close", { bubbles: true });
  }

  // Hoist on each toggle. rehome() rebuilds without the active search, so
  // re-apply it afterwards.
  _rehomeInstant() {
    this.rehome();
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim() !== "") {
      this.search();
    }
  }

  // The next visible row in place (else the previous), read before the reshuffle
  // so focus stays put instead of trailing the hoisted row into "selected".
  _nextFocusAfterToggle(toggled) {
    if (!toggled || !this._focusableRows.includes(toggled)) return null;

    const visible = this._visibleFocusableRows;
    const index = visible.indexOf(toggled);
    if (index === -1) return null;

    return visible[index + 1] || visible[index - 1] || null;
  }

  _restoreFocusAfterToggle(target) {
    if (!target || !target.isConnected) return;

    const item = target.closest("[data-filter-option-list-target='item']") || target;
    if (item.hidden) return;

    this._focusAndRove(target);
  }

  // Reveals a row that just hoisted into the selected section and pulses it, so
  // the move registers even when it lands below that section's capped scroll.
  _flashHoistedItem(item) {
    if (!item || !item.isConnected) return;

    if (typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest" });
    }

    const FLASH_CLASS = "filter-option-list__item--flash";
    // Drop + reflow so a repeat selection of the same row restarts the pulse.
    item.classList.remove(FLASH_CLASS);
    void item.offsetWidth;
    item.classList.add(FLASH_CLASS);

    // Clear via timer, not animationend: under prefers-reduced-motion the
    // animation never runs (so animationend never fires), and the per-item
    // clearTimeout stops repeat selections from stacking pending removals.
    clearTimeout(this._flashTimers.get(item));
    this._flashTimers.set(item, setTimeout(() => {
      item.classList.remove(FLASH_CLASS);
      this._flashTimers.delete(item);
    }, FLASH_FADE_MS));
  }

  // Enter flips the focused row against its applied baseline (defaultChecked),
  // not its live state, so a space/click selection survives. change fires only
  // when the state actually moves.
  _applyEnterSelection(checkbox) {
    const desired = !checkbox.defaultChecked;
    if (checkbox.checked === desired) return;

    checkbox.checked = desired;
    checkbox.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Single-select focuses on the <li>; multi-select focuses on the checkbox.
  get _focusableRows() {
    if (this.selectionModeValue === "single") {
      return this.singleRows;
    }
    return this.checkboxes;
  }

  get _visibleFocusableRows() {
    return this._focusableRows.filter((el) => {
      const item = el.closest("[data-filter-option-list-target='item']") || el;
      return !item.hidden;
    });
  }

  _focusAndRove(el) {
    this._focusableRows.forEach((r) => {
      r.setAttribute("tabindex", r === el ? "0" : "-1");
    });
    el.focus();
  }

  // Collapses the roving tab stop to a single visible row after
  // filter/reshuffle. Independently server-rendered fragments (a remote panel's
  // selected section and its lazily-loaded results frame) can each ship their
  // own tabindex=0 row, so this also de-dupes extra stops down to one.
  _resetRovingTabindex() {
    const rows = this._focusableRows;
    if (rows.length === 0) return;

    const isVisible = (r) => {
      const item = r.closest("[data-filter-option-list-target='item']") || r;
      // A row removed earlier in this turn can still be a target until Stimulus
      // notices, and must not keep the only tab stop.
      return item.isConnected && !item.hidden;
    };
    const stops = rows.filter((r) => r.getAttribute("tabindex") === "0");
    const visibleStop = stops.find(isVisible);
    // Already exactly one visible stop and nothing extra: leave it be.
    if (visibleStop && stops.length === 1) return;

    const nextStop = visibleStop || this._visibleFocusableRows[0] || rows[0];
    rows.forEach((r) => r.setAttribute("tabindex", r === nextStop ? "0" : "-1"));
  }

  // Watch the trailing sentinel; re-arm from frameLoaded after each page. Disconnect
  // first so a remote search replace cannot accumulate detached sentinels.
  _observeNextPageSentinel() {
    if (!this.hasResultsFrameTarget) return;

    if (this._nextPageObserver) {
      this._nextPageObserver.disconnect();
    }

    if (!this.hasNextPageSentinelTarget) return;

    if (!this._nextPageObserver) {
      this._nextPageObserver = new IntersectionObserver(
        (entries) => entries.forEach((entry) => this._loadNextPage(entry)),
        { root: this.resultsFrameTarget, rootMargin: "200px" },
      );
    }

    this._nextPageObserver.observe(this.nextPageSentinelTarget);
  }

  _loadNextPage(entry) {
    if (!entry.isIntersecting) return;

    const sentinel = entry.target;
    this._nextPageObserver.unobserve(sentinel);

    const nextSrc = sentinel.dataset.nextSrc;
    if (!nextSrc) return;

    // The sentinel froze selected[] when its page was server-rendered. Refresh
    // it from the live selection so deselecting a row mid-scroll stops excluding
    // options the user no longer has selected on subsequent pages.
    const url = new URL(nextSrc, window.location.origin);
    this._applySelectedParams(url);
    const src = this._serializeUrl(url);

    // Demote the sentinel to a plain frame before it loads: Turbo keeps this
    // element as the wrapper for the next page, and the response carries its own
    // sentinel, so leaving the target/src here would double-count or re-fire.
    delete sentinel.dataset.nextSrc;
    sentinel.removeAttribute("data-filter-option-list-target");
    sentinel.setAttribute("src", src);
  }

  _remoteSearch() {
    if (!this.hasResultsFrameTarget) return;
    this._persistRemoteCheckedItems();

    if (this.hasSearchClearTarget) {
      this.searchClearTarget.hidden = this.searchInputTarget.value === "";
    }

    clearTimeout(this.remoteSearchTimeout);
    this.remoteSearchTimeout = setTimeout(() => {
      if (!this.hasResultsFrameTarget) return;
      if (this.emptyModeValue === "search-first" && this._searchQuery === "") {
        this.resultsFrameTarget.removeAttribute("src");
        this.resultsFrameTarget.innerHTML = "";
        return;
      }

      this.resultsFrameTarget.setAttribute("src", this._remoteSearchUrl());
    }, this.debounceValue);
  }

  _remoteSearchUrl() {
    const url = new URL(this.urlValue, window.location.origin);

    url.searchParams.set("q", this._searchQuery);
    this._applySelectedParams(url);

    return this._serializeUrl(url);
  }

  // Rewrites selected[] on a URL to match the live selection, so every remote
  // request (search and pagination) excludes exactly the currently-selected rows.
  _applySelectedParams(url) {
    url.searchParams.delete("selected[]");
    this._excludedRemoteValues().forEach((value) => {
      url.searchParams.append("selected[]", value);
    });
  }

  // In-flight responses still carry the old selected[]. Drop rows already
  // shown above the frame so they don't reappear unchecked.
  _dropExcludedResultRows() {
    if (!this.hasUrlValue || !this.hasResultsFrameTarget) return;

    const excluded = this._excludedRemoteValues();
    if (excluded.size === 0) return;

    this.resultsFrameTarget
      .querySelectorAll("[data-filter-option-list-target~='item']")
      .forEach((item) => {
        const value = this._itemValue(item);
        if (value && excluded.has(value)) item.remove();
      });
  }

  // Selected rows are excluded because they render above the frame. Pinned rows
  // stay there even after uncheck, so they stay excluded too.
  _excludedRemoteValues() {
    const values = new Set(this.selectedValues);
    this.element.querySelectorAll("[data-pinned='true']").forEach((item) => {
      if (this.hasResultsFrameTarget && this.resultsFrameTarget.contains(item)) return;

      const value = this._itemValue(item);
      if (value) values.add(value);
    });
    return values;
  }

  _itemValue(item) {
    if (this.selectionModeValue === "single") return item.dataset.value || "";

    const checkbox = item.querySelector("input[type='checkbox']");
    return checkbox ? checkbox.value : "";
  }

  _serializeUrl(url) {
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  }

  get _searchQuery() {
    if (!this.hasSearchInputTarget) return "";

    return (this.searchInputTarget.value || "").trim();
  }

  _syncRemoteSelection(event) {
    const checkbox = event && event.target;
    if (!checkbox || checkbox.type !== "checkbox") return;

    const item = checkbox.closest("[data-filter-option-list-target='item']");
    if (!item) return;

    if (checkbox.checked) {
      this._moveRemoteItemToSelected(item);
    } else if (this._remoteSelectedSection && this._remoteSelectedSection.contains(item)) {
      item.remove();
      this._removeEmptyRemoteSelectedSection();
      this._reloadRemoteFromStart();
    }
  }

  // Single-select keeps at most one row in the selected section above the frame.
  // Dropping the prior row reloads results from page 1 with the new selection.
  _syncRemoteSingleSelection(nextSelected) {
    const section = this._remoteSelectedSection;
    let droppedSelection = false;
    if (section) {
      Array.from(section.querySelectorAll("[data-filter-option-list-target='item']"))
        .forEach((item) => {
          if (item === nextSelected) return;

          item.remove();
          droppedSelection = true;
        });
    }

    if (!nextSelected) {
      this._removeEmptyRemoteSelectedSection();
    } else {
      const target = this._ensureRemoteSelectedSection();
      if (target && !target.contains(nextSelected)) target.appendChild(nextSelected);
    }

    if (droppedSelection) this._reloadRemoteFromStart();
  }

  // A deselected row may sort before the loaded cursor, so later pages can
  // never return it. Start over from page 1 with the current selection.
  _reloadRemoteFromStart() {
    if (!this.hasResultsFrameTarget) return;

    if (this.emptyModeValue === "search-first" && this._searchQuery === "") {
      this.resultsFrameTarget.removeAttribute("src");
      this.resultsFrameTarget.innerHTML = "";
      return;
    }

    const src = this._remoteSearchUrl();
    if (this.resultsFrameTarget.getAttribute("src") === src) {
      this.resultsFrameTarget.removeAttribute("src");
    }
    this.resultsFrameTarget.setAttribute("src", src);
  }

  _persistRemoteCheckedItems() {
    this.itemTargets.forEach((item) => {
      if (!this.hasResultsFrameTarget || !this.resultsFrameTarget.contains(item)) return;

      const checkbox = item.querySelector("input[type='checkbox']");
      if (checkbox && checkbox.checked) {
        this._moveRemoteItemToSelected(item);
      }
    });
  }

  _moveRemoteItemToSelected(item) {
    const section = this._ensureRemoteSelectedSection();
    if (!section || section.contains(item)) return;

    const checkbox = item.querySelector("input[type='checkbox']");
    const existing = Array.from(section.querySelectorAll("input[type='checkbox']"))
      .find((existingCheckbox) => existingCheckbox.value === checkbox.value);
    if (existing) {
      item.remove();
      return;
    }

    this._insertItemSorted(section, item);
  }

  _insertItemSorted(section, item) {
    const itemName = (item.dataset.name || "").toLowerCase();
    const before = Array.from(section.querySelectorAll("[data-filter-option-list-target='item']"))
      .find((existing) => (existing.dataset.name || "").toLowerCase().localeCompare(itemName) > 0);

    section.insertBefore(item, before || null);
  }

  _ensureRemoteSelectedSection() {
    if (this._remoteSelectedSection) return this._remoteSelectedSection;

    const section = this._createSection({ kind: "selected", index: -1 });
    const container = this._remoteSelectedContainer;
    if (!container) return null;

    if (container.querySelector("[data-section-kind='pinned']")) {
      container.appendChild(this._createDivider());
    }
    container.appendChild(section);
    return section;
  }

  _removeEmptyRemoteSelectedSection() {
    const section = this._remoteSelectedSection;
    if (!section || section.children.length > 0) return;

    const previous = section.previousElementSibling;
    if (previous && previous.classList.contains("filter-option-list__divider")) previous.remove();
    section.remove();
  }

  get _remoteSelectedSection() {
    return this.selectedSectionTargets.find((section) => {
      return !this.hasResultsFrameTarget || !this.resultsFrameTarget.contains(section);
    });
  }

  get _remoteSelectedContainer() {
    if (!this.hasResultsFrameTarget) return null;

    const existing = this.resultsFrameTarget.previousElementSibling;
    if (existing && existing.classList.contains("filter-option-list__sections")) {
      return existing;
    }

    const container = document.createElement("div");
    container.className = "filter-option-list__sections";
    if (this.selectionModeValue === "single") container.setAttribute("role", "listbox");
    this.resultsFrameTarget.parentNode.insertBefore(container, this.resultsFrameTarget);
    return container;
  }

  _createSection({ kind, index }) {
    const ul = document.createElement("ul");
    ul.className = "filter-option-list__section";
    const targets = kind === "selected" ? "section selectedSection" : "section";
    ul.setAttribute("data-filter-option-list-target", targets);
    ul.setAttribute("data-section-kind", kind);
    ul.setAttribute("data-section-index", String(index));
    // Single-select flattens the listbox tree: the container is the listbox and
    // the inner <ul>s are presentational (matches the server markup).
    if (this.selectionModeValue === "single") ul.setAttribute("role", "presentation");
    return ul;
  }

  _createDivider() {
    const d = document.createElement("div");
    d.className = "filter-option-list__divider";
    d.setAttribute("data-filter-option-list-target", "divider");
    d.setAttribute("role", "presentation");
    return d;
  }
}
